// src/services/tables/playerColumns.js - FIXED TO USE BACKEND FIELD NAMES

import { dynastyTheme } from '../colorService';
import { renderDefault, renderFloat1, renderFloat2, renderFloat3, renderAvg } from './DynastyTable';
import { formatBattingAvg, formatERA } from '../statFormatUtils';

// =============================================================================
// FREE AGENT HITTER COLUMNS
// =============================================================================
// =============================================================================
export const createCareerStatsColumns = (isPitcher = false) => {
  // Accessor function for nested pitching data in career stats
  const pitchingAccessor = (statName) => (row) => {
    return row?.pitching?.[statName] ?? 0;
  };

  // Accessor function for nested hitting data in career stats
  const hittingAccessor = (statName) => (row) => {
    return row?.hitting?.[statName] ?? 0;
  };

  if (isPitcher) {
    return [
      { key: 'season', title: 'Year', width: 40, render: (player, rowType, slotInfo) => player.season || '-' },
      { key: 'mlb_team', title: 'Tm', width: 35, render: (player, rowType, slotInfo) => player.info?.mlb_team || '-' },
      {
        key: 'games_played',
        title: 'G',
        width: 28,
        accessor: pitchingAccessor('games_played'),
        render: (player, rowType, slotInfo) => { const row = player; return renderDefault(pitchingAccessor('games_played')(row)); }
      },
      { 
        key: 'games_started', 
        title: 'GS', 
        width: 32, 
        accessor: pitchingAccessor('games_started'),
        render: (player, rowType, slotInfo) => { const row = player; return renderDefault(pitchingAccessor('games_started')(row)); }
      },
      { 
        key: 'innings_pitched', 
        title: 'IP', 
        width: 42, 
        accessor: pitchingAccessor('innings_pitched'),
        render: (player, rowType, slotInfo) => { const row = player; return parseFloat(pitchingAccessor('innings_pitched')(row)).toFixed(1); }
      },
      {
        key: 'era',
        title: 'ERA',
        width: 42,
        accessor: pitchingAccessor('era'),
        render: (player) => formatERA(pitchingAccessor('era')(player))
      },
      {
        key: 'whip',
        title: 'WHIP',
        width: 48,
        accessor: pitchingAccessor('whip'),
        render: (player) => formatBattingAvg(pitchingAccessor('whip')(player))
      },
      { 
        key: 'wins', 
        title: 'W', 
        width: 28, 
        accessor: pitchingAccessor('wins'),
        render: (player, rowType, slotInfo) => { const row = player; return renderDefault(pitchingAccessor('wins')(row)); }
      },
      { 
        key: 'losses', 
        title: 'L', 
        width: 28, 
        accessor: pitchingAccessor('losses'),
        render: (player, rowType, slotInfo) => { const row = player; return renderDefault(pitchingAccessor('losses')(row)); }
      },
      { 
        key: 'saves', 
        title: 'SV', 
        width: 32, 
        accessor: pitchingAccessor('saves'),
        render: (player, rowType, slotInfo) => { const row = player; return renderDefault(pitchingAccessor('saves')(row)); }
      },
      { 
        key: 'quality_starts', 
        title: 'QS', 
        width: 32, 
        accessor: pitchingAccessor('quality_starts'),
        render: (player, rowType, slotInfo) => { const row = player; return renderDefault(pitchingAccessor('quality_starts')(row)); }
      },
      { 
        key: 'strikeouts_pitched', 
        title: 'SO', 
        width: 32, 
        accessor: pitchingAccessor('strikeouts_pitched'),
        render: (player, rowType, slotInfo) => { const row = player; return renderDefault(pitchingAccessor('strikeouts_pitched')(row)); }
      },
      { 
        key: 'hits_allowed', 
        title: 'H', 
        width: 32, 
        accessor: pitchingAccessor('hits_allowed'),
        render: (player, rowType, slotInfo) => { const row = player; return renderDefault(pitchingAccessor('hits_allowed')(row)); }
      },
      { 
        key: 'walks_allowed', 
        title: 'BB', 
        width: 32, 
        accessor: pitchingAccessor('walks_allowed'),
        render: (player, rowType, slotInfo) => { const row = player; return renderDefault(pitchingAccessor('walks_allowed')(row)); }
      }
    ];
  }

  return [
    { key: 'season', title: 'Year', width: 40, render: (player, rowType, slotInfo) => player.season || '-' },
    { key: 'mlb_team', title: 'Tm', width: 35, render: (player, rowType, slotInfo) => player.info?.mlb_team || '-' },
    {
      key: 'games_played',
      title: 'G',
      width: 28,
      accessor: hittingAccessor('games_played'),
      render: (player, rowType, slotInfo) => { const row = player; return renderDefault(hittingAccessor('games_played')(row)); }
    },
    { 
      key: 'at_bats', 
      title: 'AB', 
      width: 32, 
      accessor: hittingAccessor('at_bats'),
      render: (player, rowType, slotInfo) => { const row = player; return renderDefault(hittingAccessor('at_bats')(row)); }
    },
    { 
      key: 'runs', 
      title: 'R', 
      width: 28, 
      accessor: hittingAccessor('runs'),
      render: (player, rowType, slotInfo) => { const row = player; return renderDefault(hittingAccessor('runs')(row)); }
    },
    { 
      key: 'hits', 
      title: 'H', 
      width: 28, 
      accessor: hittingAccessor('hits'),
      render: (player, rowType, slotInfo) => { const row = player; return renderDefault(hittingAccessor('hits')(row)); }
    },
    { 
      key: 'doubles', 
      title: '2B', 
      width: 28, 
      accessor: hittingAccessor('doubles'),
      render: (player, rowType, slotInfo) => { const row = player; return renderDefault(hittingAccessor('doubles')(row)); }
    },
    { 
      key: 'triples', 
      title: '3B', 
      width: 28, 
      accessor: hittingAccessor('triples'),
      render: (player, rowType, slotInfo) => { const row = player; return renderDefault(hittingAccessor('triples')(row)); }
    },
    { 
      key: 'home_runs', 
      title: 'HR', 
      width: 28, 
      accessor: hittingAccessor('home_runs'),
      render: (player, rowType, slotInfo) => { const row = player; return renderDefault(hittingAccessor('home_runs')(row)); }
    },
    { 
      key: 'rbi', 
      title: 'RBI', 
      width: 32, 
      accessor: hittingAccessor('rbi'),
      render: (player, rowType, slotInfo) => { const row = player; return renderDefault(hittingAccessor('rbi')(row)); }
    },
    { 
      key: 'stolen_bases', 
      title: 'SB', 
      width: 28, 
      accessor: hittingAccessor('stolen_bases'),
      render: (player, rowType, slotInfo) => { const row = player; return renderDefault(hittingAccessor('stolen_bases')(row)); }
    },
    { 
      key: 'walks', 
      title: 'BB', 
      width: 28, 
      accessor: hittingAccessor('walks'),
      render: (player, rowType, slotInfo) => { const row = player; return renderDefault(hittingAccessor('walks')(row)); }
    },
    { 
      key: 'strikeouts', 
      title: 'K', 
      width: 28, 
      accessor: hittingAccessor('strikeouts'),
      render: (player, rowType, slotInfo) => { const row = player; return renderDefault(hittingAccessor('strikeouts')(row)); }
    },
    {
      key: 'batting_avg',
      title: 'AVG',
      width: 40,
      accessor: hittingAccessor('batting_avg'),
      render: (player) => formatBattingAvg(hittingAccessor('batting_avg')(player))
    },
    {
      key: 'obp',
      title: 'OBP',
      width: 40,
      accessor: hittingAccessor('obp'),
      render: (player) => formatBattingAvg(hittingAccessor('obp')(player))
    },
    {
      key: 'slg',
      title: 'SLG',
      width: 40,
      accessor: hittingAccessor('slg'),
      render: (player) => formatBattingAvg(hittingAccessor('slg')(player))
    },
    {
      key: 'ops',
      title: 'OPS',
      width: 40,
      accessor: hittingAccessor('ops'),
      render: (player) => formatBattingAvg(hittingAccessor('ops')(player))
    }
  ];
};

// =============================================================================
// GAME LOGS COLUMNS - CANONICAL NESTED STRUCTURE (hitting/pitching objects)
// =============================================================================
export const createGameLogsColumns = (isPitcher = false) => {
  const dateRender = (value) => {
    if (!value) return '-';
    if (typeof value === 'string' && value === 'TOTAL') return 'TOTAL';
    // Parse YYYY-MM-DD string directly to avoid timezone shift
    // new Date("2026-03-26") = UTC midnight, which in ET shows as 3/25
    const parts = String(value).split('-');
    if (parts.length === 3) {
      return `${parseInt(parts[1])}/${parseInt(parts[2])}`;
    }
    const date = new Date(value);
    return `${(date.getUTCMonth() + 1)}/${date.getUTCDate()}`;
  };

  const oppRender = (value, row) => {
    if (!value) return '-';
    const homeAway = row.home_away === 'H' ? 'v' : '@';  // Shorter prefix
    return `${homeAway}${value}`;
  };

  // Accessor function for nested pitching data
  const pitchingAccessor = (statName) => (row) => {
    return row?.pitching?.[statName] ?? 0;
  };

  // Accessor function for nested hitting data
  const hittingAccessor = (statName) => (row) => {
    return row?.hitting?.[statName] ?? 0;
  };

  const neutralHeader = dynastyTheme.classes.text.neutral;

  if (isPitcher) {
    // DD-STAT-CONSOLIDATION: pitcher game log now renders the LOCKED pitching set in
    // canonical order (all fields present in the game-log JSONB) + computed ERA/WHIP.
    // ERA/WHIP are derived from the row's pitching components, so the SAME render works
    // for a per-game row AND the summed TOTAL row (its pitching object carries the sums).
    const pCol = (key, title, width, statName) => ({
      key, title, width, titleClass: neutralHeader,
      accessor: pitchingAccessor(statName),
      render: (player) => renderDefault(pitchingAccessor(statName)(player)),
    });
    const eraFrom = (p) => {
      const ip = parseFloat(p?.innings_pitched) || 0;
      return ip > 0 ? formatERA(((p.earned_runs || 0) * 9) / ip) : '-';
    };
    const whipFrom = (p) => {
      const ip = parseFloat(p?.innings_pitched) || 0;
      return ip > 0 ? (((p.hits_allowed || 0) + (p.walks_allowed || 0)) / ip).toFixed(3) : '-';
    };
    return [
      {
        key: 'game_date',
        title: 'Date',
        width: 35,
        sortable: true,
        titleClass: neutralHeader,
        accessor: (row) => row.game_date === 'TOTAL' ? Infinity : (row.game_date ? new Date(row.game_date).getTime() : 0),
        render: (player, rowType, slotInfo) => dateRender(player.game_date)
      },
      { key: 'opponent', title: 'Opp', width: 40, titleClass: neutralHeader, render: (player, rowType, slotInfo) => oppRender(player.opponent, player) },
      { key: 'mlb_team', title: 'Tm', width: 28, titleClass: neutralHeader, render: (player, rowType, slotInfo) => player.mlb_team || '-' },
      // Canonical pitching order: GS, G, W, L, SV, HLD, BS, QS, IP, H, R, ER, HR, BB, K, ERA, WHIP
      pCol('games_started', 'GS', 22, 'games_started'),
      {
        // Games: each game-log row is one game; the TOTAL row carries the count.
        key: 'games',
        title: 'G',
        width: 20,
        titleClass: neutralHeader,
        // TOTAL row: desktop game-log sets games_total (row count); the mobile modal's
        // season TOT row instead carries season pitching.games_played — fall back to it.
        accessor: (row) => row.game_date === 'TOTAL' ? (row.games_total ?? row.pitching?.games_played ?? 0) : 1,
        render: (player) => player.game_date === 'TOTAL' ? String(player.games_total ?? player.pitching?.games_played ?? 0) : '1'
      },
      pCol('wins', 'W', 20, 'wins'),
      pCol('losses', 'L', 20, 'losses'),
      pCol('saves', 'SV', 22, 'saves'),
      pCol('holds', 'HLD', 26, 'holds'),
      pCol('blown_saves', 'BS', 22, 'blown_saves'),
      pCol('quality_starts', 'QS', 22, 'quality_starts'),
      {
        key: 'innings_pitched',
        title: 'IP',
        width: 28,
        titleClass: neutralHeader,
        accessor: pitchingAccessor('innings_pitched'),
        render: (player) => parseFloat(pitchingAccessor('innings_pitched')(player)).toFixed(1)
      },
      pCol('hits_allowed', 'H', 20, 'hits_allowed'),
      pCol('runs_allowed', 'R', 20, 'runs_allowed'),
      pCol('earned_runs', 'ER', 22, 'earned_runs'),
      pCol('home_runs_allowed', 'HR', 22, 'home_runs_allowed'),
      pCol('walks_allowed', 'BB', 22, 'walks_allowed'),
      pCol('strikeouts_pitched', 'K', 20, 'strikeouts_pitched'),
      {
        key: 'era',
        title: 'ERA',
        width: 34,
        titleClass: neutralHeader,
        accessor: (row) => { const p = row.pitching || {}; const ip = parseFloat(p.innings_pitched) || 0; return ip > 0 ? ((p.earned_runs || 0) * 9) / ip : Infinity; },
        render: (player) => eraFrom(player.pitching || {})
      },
      {
        key: 'whip',
        title: 'WHIP',
        width: 40,
        titleClass: neutralHeader,
        accessor: (row) => { const p = row.pitching || {}; const ip = parseFloat(p.innings_pitched) || 0; return ip > 0 ? ((p.hits_allowed || 0) + (p.walks_allowed || 0)) / ip : Infinity; },
        render: (player) => whipFrom(player.pitching || {})
      }
    ];
  }

  return [
    {
      key: 'game_date',
      title: 'Date',
      width: 35,
      sortable: true,
      titleClass: neutralHeader,
      accessor: (row) => row.game_date === 'TOTAL' ? Infinity : (row.game_date ? new Date(row.game_date).getTime() : 0),
      render: (player, rowType, slotInfo) => dateRender(player.game_date)
    },
    { key: 'opponent', title: 'Opp', width: 40, titleClass: neutralHeader, render: (player, rowType, slotInfo) => oppRender(player.opponent, player) },
    { key: 'mlb_team', title: 'Tm', width: 28, titleClass: neutralHeader, render: (player, rowType, slotInfo) => player.info?.mlb_team || '-' },
    {
      key: 'at_bats',
      title: 'AB',
      width: 22,
      titleClass: neutralHeader,
      accessor: hittingAccessor('at_bats'),
      render: (player, rowType, slotInfo) => renderDefault(hittingAccessor('at_bats')(player))
    },
    {
      key: 'runs',
      title: 'R',
      width: 18,
      titleClass: neutralHeader,
      accessor: hittingAccessor('runs'),
      render: (player, rowType, slotInfo) => renderDefault(hittingAccessor('runs')(player))
    },
    {
      key: 'hits',
      title: 'H',
      width: 18,
      titleClass: neutralHeader,
      accessor: hittingAccessor('hits'),
      render: (player, rowType, slotInfo) => renderDefault(hittingAccessor('hits')(player))
    },
    {
      key: 'doubles',
      title: '2B',
      width: 20,
      titleClass: neutralHeader,
      accessor: hittingAccessor('doubles'),
      render: (player, rowType, slotInfo) => renderDefault(hittingAccessor('doubles')(player))
    },
    {
      key: 'triples',
      title: '3B',
      width: 20,
      titleClass: neutralHeader,
      accessor: hittingAccessor('triples'),
      render: (player, rowType, slotInfo) => renderDefault(hittingAccessor('triples')(player))
    },
    {
      key: 'home_runs',
      title: 'HR',
      width: 22,
      titleClass: neutralHeader,
      accessor: hittingAccessor('home_runs'),
      render: (player, rowType, slotInfo) => renderDefault(hittingAccessor('home_runs')(player))
    },
    {
      key: 'rbi',
      title: 'RBI',
      width: 24,
      titleClass: neutralHeader,
      accessor: hittingAccessor('rbi'),
      render: (player, rowType, slotInfo) => renderDefault(hittingAccessor('rbi')(player))
    },
    // DD-STAT-CONSOLIDATION: canonical order — BB before SB/CS (CS kept as an
    // industry-standard game-log extra), then K.
    {
      key: 'walks',
      title: 'BB',
      width: 20,
      titleClass: neutralHeader,
      accessor: hittingAccessor('walks'),
      render: (player, rowType, slotInfo) => renderDefault(hittingAccessor('walks')(player))
    },
    {
      key: 'stolen_bases',
      title: 'SB',
      width: 20,
      titleClass: neutralHeader,
      accessor: hittingAccessor('stolen_bases'),
      render: (player, rowType, slotInfo) => renderDefault(hittingAccessor('stolen_bases')(player))
    },
    {
      key: 'caught_stealing',
      title: 'CS',
      width: 20,
      titleClass: neutralHeader,
      accessor: hittingAccessor('caught_stealing'),
      render: (player, rowType, slotInfo) => renderDefault(hittingAccessor('caught_stealing')(player))
    },
    {
      key: 'strikeouts',
      title: 'K',
      width: 18,
      titleClass: neutralHeader,
      accessor: hittingAccessor('strikeouts'),
      render: (player, rowType, slotInfo) => renderDefault(hittingAccessor('strikeouts')(player))
    },
    // SEASON RUNNING slash line (season-to-date through this game): AVG / OBP / SLG /
    // OPS — the complete industry-standard slash line. SLG was previously MISSING; it's
    // derived exactly as OPS − OBP (both already carried on season_stats), so no backend
    // change is needed. Missing season_stats → honest '-'.
    {
      key: 'season_avg',
      title: 'AVG',
      width: 42,
      accessor: (row) => row?.season_stats?.avg ?? 0,
      render: (player) => (
        <span className={`${dynastyTheme.classes.text.primary} font-semibold`}>
          {formatBattingAvg(player?.season_stats?.avg)}
        </span>
      )
    },
    {
      key: 'season_obp',
      title: 'OBP',
      width: 42,
      accessor: (row) => row?.season_stats?.obp ?? 0,
      render: (player) => (
        <span className={`${dynastyTheme.classes.text.primary} font-semibold`}>
          {formatBattingAvg(player?.season_stats?.obp)}
        </span>
      )
    },
    {
      key: 'season_slg',
      title: 'SLG',
      width: 42,
      accessor: (row) => {
        const ss = row?.season_stats;
        return (ss && ss.ops != null && ss.obp != null) ? (ss.ops - ss.obp) : 0;
      },
      render: (player) => {
        const ss = player?.season_stats;
        const slg = (ss && ss.ops != null && ss.obp != null) ? (ss.ops - ss.obp) : null;
        return (
          <span className={`${dynastyTheme.classes.text.primary} font-semibold`}>
            {slg == null ? '-' : formatBattingAvg(slg)}
          </span>
        );
      }
    },
    {
      key: 'season_ops',
      title: 'OPS',
      width: 42,
      accessor: (row) => row?.season_stats?.ops ?? 0,
      render: (player) => (
        <span className={`${dynastyTheme.classes.text.primary} font-semibold`}>
          {formatBattingAvg(player?.season_stats?.ops)}
        </span>
      )
    }
  ];
};